/**
 * WarpFieldSystem
 *
 * Owns two `rgba16float` displacement textures (called `current` and
 * `previous`) plus the regenerator pass. On each frame the engine calls
 * `step(now, audio)` and the system:
 *
 *   1. Advances the in-flight crossfade toward t = 1, regenerating the
 *      previous slot once the crossfade completes.
 *   2. Decides whether to start a new adoption (beat above threshold,
 *      cooldown elapsed, no crossfade currently running). On adoption it
 *      swaps the slots and seeds a new staging field generation.
 *
 * The warp shader samples both textures every frame and lerps using `t`,
 * so the visible motion never snaps to a different field abruptly.
 *
 * Two-texture lifecycle:
 *   - At construction: both textures are seeded with fields generated from
 *     the preset config; `current` is the displayed one, `previous` is
 *     identical (so t doesn't matter until the first adoption).
 *   - On adoption: the slot pointers swap, t resets to 0, and the engine
 *     crossfades from previous (the OLD current) to the NEW current. When
 *     the crossfade finishes, the now-stale previous slot is regenerated
 *     with a fresh seed offset so the next adoption looks different.
 */

import type { WarpFieldConfig } from './types';
import {
  FieldGeneratorId,
  WarpFieldGenPass,
  type FieldGenInput,
} from '../../renderer/webgpu/passes/warpFieldGenPass';

const FIELD_FORMAT: GPUTextureFormat = 'rgba16float';
export const DEFAULT_FIELD_RESOLUTION = 256;

export type WarpFieldState = {
  /** [0, 1]; 1 means stable on `currentView`. */
  t: number;
  /** Time since last adoption (ms). null means never adopted. */
  msSinceLastAdoption: number | null;
  /** True while a crossfade is in flight. */
  inTransition: boolean;
  /** Generator id currently in the active slot. */
  generator: 'curl' | 'spiral' | 'swirl' | 'julia';
};

export class WarpFieldSystem {
  readonly currentView: GPUTextureView;
  readonly previousView: GPUTextureView;
  // Internal — these two never get re-pointed; we toggle which is "current"
  // via the activeIdx instead.
  private readonly textures: [GPUTexture, GPUTexture];
  private readonly views: [GPUTextureView, GPUTextureView];

  private activeIdx: 0 | 1 = 0;
  private t = 1;
  private transitionStartMs = 0;
  private transitionDurationMs = 400;
  private lastAdoptionMs = -Infinity;
  private seedOffset = 0;
  private regenerationQueued = false;

  private readonly device: GPUDevice;
  private readonly genPass: WarpFieldGenPass;
  private readonly resolution: number;

  constructor(
    device: GPUDevice,
    genPass: WarpFieldGenPass,
    private config: WarpFieldConfig,
    resolution: number = DEFAULT_FIELD_RESOLUTION,
  ) {
    this.device = device;
    this.genPass = genPass;
    this.resolution = resolution;

    const texA = device.createTexture({
      label: 'warp-field-A',
      size: { width: resolution, height: resolution },
      format: FIELD_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    const texB = device.createTexture({
      label: 'warp-field-B',
      size: { width: resolution, height: resolution },
      format: FIELD_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.textures = [texA, texB];
    this.views = [texA.createView(), texB.createView()];

    // Seed both slots with the initial field so the first frame samples
    // sensible data even before any adoption.
    this.regenerateInto(this.views[0], 0);
    this.regenerateInto(this.views[1], 0);

    // Defined here so they're stable references even though the *contents*
    // depend on activeIdx. The accessors below don't reuse these.
    this.currentView = this.views[0];
    this.previousView = this.views[1];
  }

  destroy(): void {
    for (const t of this.textures) t.destroy();
  }

  /** View the warp shader should sample as the "fully-adopted" field. */
  getCurrentView(): GPUTextureView {
    // activeIdx is the typed `0 | 1`, and `views` always has length 2; the
    // tuple type means TS still narrows to GPUTextureView.
    return this.activeIdx === 0 ? this.views[0] : this.views[1];
  }
  getPreviousView(): GPUTextureView {
    return this.activeIdx === 0 ? this.views[1] : this.views[0];
  }

  /** Crossfade weight in [0, 1]; warp shader uses this as `fieldT`. */
  getT(): number {
    return this.t;
  }

  setConfig(config: WarpFieldConfig): void {
    const generatorChanged = this.config.generator !== config.generator;
    this.config = config;
    if (generatorChanged) {
      // New generator entirely — reseed both slots so the field doesn't
      // jump from one shape to another mid-frame.
      this.seedOffset = 0;
      this.t = 1;
      this.regenerateInto(this.views[0], 0);
      this.regenerateInto(this.views[1], 0);
    }
  }

  /**
   * Drive the system one frame. Call once per visible frame, after the
   * audio analyser has produced the current AudioFrame.
   */
  step(
    encoder: GPUCommandEncoder,
    nowMs: number,
    beatTick: boolean,
    beatConfidence: number,
  ): WarpFieldState {
    if (this.t < 1) {
      const elapsed = nowMs - this.transitionStartMs;
      this.t = clamp01(elapsed / this.transitionDurationMs);
      if (this.t >= 1 && !this.regenerationQueued) {
        this.regenerationQueued = true;
      }
    }

    if (this.regenerationQueued) {
      // Crossfade finished — generate a fresh field into the (now stale)
      // previous slot so the next adoption produces a new shape.
      const regen = this.config.adoption.regenerateOnAdopt ?? true;
      if (regen) {
        this.seedOffset += 1;
      }
      const stale: 0 | 1 = this.activeIdx === 0 ? 1 : 0;
      this.encodeRegeneration(encoder, stale);
      this.regenerationQueued = false;
    }

    const cooldownOk = nowMs - this.lastAdoptionMs >= this.config.adoption.cooldownMs;
    if (
      this.t >= 1 &&
      beatTick &&
      beatConfidence >= this.config.adoption.beatThreshold &&
      cooldownOk
    ) {
      this.startAdoption(encoder, nowMs);
    }

    return {
      t: this.t,
      msSinceLastAdoption: Number.isFinite(this.lastAdoptionMs)
        ? nowMs - this.lastAdoptionMs
        : null,
      inTransition: this.t < 1,
      generator: this.config.generator,
    };
  }

  private startAdoption(_encoder: GPUCommandEncoder, nowMs: number): void {
    // Swap which slot is the "current" one. The view that *was* current
    // becomes the previous (the one we crossfade FROM); the new current
    // is whatever was sitting in staging.
    this.activeIdx = this.activeIdx === 0 ? 1 : 0;
    this.t = 0;
    this.transitionStartMs = nowMs;
    this.transitionDurationMs = this.config.adoption.crossfadeMs;
    this.lastAdoptionMs = nowMs;
  }

  private encodeRegeneration(encoder: GPUCommandEncoder, slot: 0 | 1): void {
    const view = slot === 0 ? this.views[0] : this.views[1];
    this.genPass.encode(encoder, view, this.makeGenInput());
  }

  private regenerateInto(view: GPUTextureView, seedOffset: number): void {
    // Used during construction — needs its own command encoder + submit
    // because there's no main frame encoder yet.
    const tempOffset = this.seedOffset;
    this.seedOffset = seedOffset;
    const enc = this.device.createCommandEncoder({ label: 'warp-field-init' });
    this.genPass.encode(enc, view, this.makeGenInput());
    this.device.queue.submit([enc.finish()]);
    this.seedOffset = tempOffset;
  }

  private makeGenInput(): FieldGenInput {
    const kind = GENERATOR_TO_ID[this.config.generator];
    // Generator-specific param routing. The WGSL GenParams struct has a
    // fixed (scale, param0, param1, param2) layout; each generator reads
    // the slots it needs. We route the preset's declared params into the
    // right slots per generator so presets stay self-documenting.
    if (kind === FieldGeneratorId.Julia) {
      // Julia: param0 = c.re, param1 = c.im, param2 = complex-plane zoom.
      return {
        kind,
        seed: this.config.seed + this.seedOffset * 7.13,
        centerX: this.config.centerX ?? 0.5,
        centerY: this.config.centerY ?? 0.5,
        scale: this.config.param2 ?? 1.5,
        param0: this.config.param0 ?? -0.7269,
        param1: this.config.param1 ?? 0.1889,
        param2: this.config.param2 ?? 1.5,
      };
    }
    return {
      kind,
      seed: this.config.seed + this.seedOffset * 7.13, // arbitrary stride
      centerX: this.config.centerX ?? 0.5,
      centerY: this.config.centerY ?? 0.5,
      scale: this.config.param0 ?? 4.0, // overloaded for curl: scales noise frequency
      param0: this.config.param0 ?? 3.0,
      param1: this.config.param1 ?? 1.5,
      param2: this.config.param2 ?? 1.0,
    };
  }

  // Exposed for HUD / debug.
  getResolution(): number {
    return this.resolution;
  }
}

const GENERATOR_TO_ID = {
  curl: FieldGeneratorId.Curl,
  spiral: FieldGeneratorId.Spiral,
  swirl: FieldGeneratorId.Swirl,
  julia: FieldGeneratorId.Julia,
} as const;

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}
